import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import Seo, { composeTitle, truncateForMeta } from "@/components/Seo";
import { Container, SectionLabel, ButtonLux } from "@/components/primitives";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";

const FALLBACK_OCCASIONS = ["Housewarming", "Birthday", "Wedding", "Anniversary", "Festive", "Corporate", "Thank You", "Other"];
const FALLBACK_BUDGETS = ["Under ₹5,000", "₹5,000 – ₹15,000", "₹15,000 – ₹50,000", "₹50,000 – ₹1,00,000", "Above ₹1,00,000", "Open — curate as you see fit"];

function Chips({ options, value, onChange, prefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(active ? "" : opt)}
            data-testid={`${prefix}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-4 py-2.5 rounded-full border text-[13px] transition-colors ${active ? "border-lux-accent bg-[rgba(196,164,88,0.10)] text-lux-accent font-medium" : "border-lux-line bg-white text-lux-sec hover:border-lux-lineStrong"}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[10px] tracking-[0.3em] uppercase text-lux-muted">{label}</label>
      {children}
      {hint ? <span className="text-xs text-lux-muted italic">{hint}</span> : null}
    </div>
  );
}

export default function GiftingCurated() {
  const [params] = useSearchParams();
  const { settings } = useSettings();
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.getPage("gifting").then(setDoc).catch(() => {}); }, []);

  const form = doc?.content?.curated_form || {};
  const occasions = Array.isArray(form.occasions) && form.occasions.length ? form.occasions : FALLBACK_OCCASIONS;
  const budgets = Array.isArray(form.budgets) && form.budgets.length ? form.budgets : FALLBACK_BUDGETS;
  const initialItem = useMemo(() => params.get("item") || "", [params]);

  const [f, setF] = useState({ recipient: "", occasion: "", budget: "", preferences: "", notes: "", name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const inputCls = "w-full border-b border-lux-line bg-transparent py-3 text-[15px] text-lux-ink outline-none focus:border-lux-ink transition-colors placeholder:text-lux-muted";

  const submit = async () => {
    setErr(null);
    if (!f.name.trim() || !f.email.trim() || !f.phone.trim()) { setErr("Please share your name, email and phone so we can respond."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) { setErr("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      await api.createGiftingEnquiry({ ...f, item_context: initialItem.trim() });
      setSubmitted(true);
    } catch (e) {
      setErr(e?.message || "Something went wrong. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const ml = (t) => (t || "").split("\n").map((line, i) => <span key={i} className="block">{line}</span>);

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo title={composeTitle("Curated Gift Collection", settings.seo.default_title, settings.seo.title_suffix)} description={truncateForMeta(form.subtitle || "Tell us who. Tell us why. We'll curate the rest.")} path="/gifting/curated" />
      <TopBar />
      <Container className="pt-32 md:pt-40 pb-24">
        <div className="max-w-[780px] mx-auto">
          <Link to="/gifting" data-testid="curated-breadcrumb" className="text-[11px] tracking-[0.25em] text-lux-muted hover:text-lux-ink transition-colors">← GIFTING</Link>
          <div className="mt-6">
            <SectionLabel>{form.eyebrow || "· Curated Gift ·"}</SectionLabel>
            <h1 className="font-serif text-lux-ink font-light tracking-tight leading-tight mt-3 text-3xl md:text-5xl">{ml(form.title || "Tell us who. Tell us why.\nWe'll curate the rest.")}</h1>
            {form.subtitle ? <p className="text-base md:text-[17px] text-lux-sec font-light leading-relaxed mt-4 max-w-[620px]">{form.subtitle}</p> : null}
          </div>

          <div className="h-12" />

          {submitted ? (
            <div className="p-8 border-t-[3px] border-[#3F7A52] bg-[rgba(63,122,82,0.06)]" data-testid="curated-success">
              <SectionLabel>· Brief Received ·</SectionLabel>
              <h2 className="font-serif text-3xl text-lux-ink font-light mt-3">{form.success_title || "Thank you. The studio is on it."}</h2>
              <p className="text-[15px] text-lux-sec leading-relaxed font-light mt-3">{form.success_body || "We've received your Curated Gift brief. Someone from the studio will reach out within one working day with a first considered selection."}</p>
              <div className="mt-6"><ButtonLux label="Back to Gifting" variant="outline" to="/gifting" testId="curated-done" /></div>
            </div>
          ) : (
            <div className="flex flex-col gap-7">
              {initialItem ? (
                <div className="p-4 bg-[rgba(196,164,88,0.08)] border-l-[3px] border-[#B8912A]" data-testid="curated-item-context">
                  <p className="text-[10px] tracking-[0.3em] text-[#8A6C1F]">ITEM OF INTEREST</p>
                  <p className="text-[15px] text-lux-ink font-serif mt-1">{initialItem}</p>
                </div>
              ) : null}

              <Field label="Who are you gifting?">
                <input data-testid="curated-recipient" value={f.recipient} onChange={(e) => set("recipient", e.target.value)} placeholder="e.g. Close friend moving into a new home" className={inputCls} />
              </Field>
              <Field label="What is the occasion?">
                <Chips options={occasions} value={f.occasion} onChange={(v) => set("occasion", v)} prefix="curated-occasion" />
              </Field>
              <Field label="Approximate budget">
                <Chips options={budgets} value={f.budget} onChange={(v) => set("budget", v)} prefix="curated-budget" />
              </Field>
              <Field label="What do they like?" hint="Colours, materials, categories they gravitate to.">
                <textarea data-testid="curated-preferences" value={f.preferences} onChange={(e) => set("preferences", e.target.value)} placeholder="Optional" rows={3} className={`${inputCls} resize-y min-h-[80px]`} />
              </Field>
              <Field label="Anything else we should know?" hint="Delivery timeline, gift note, budget flexibility.">
                <textarea data-testid="curated-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional" rows={3} className={`${inputCls} resize-y min-h-[80px]`} />
              </Field>

              <div className="h-px bg-lux-line mt-2" />
              <p className="text-[11px] tracking-[0.3em] uppercase text-lux-muted">Your Details</p>
              <div className="flex flex-col md:flex-row gap-4">
                <Field label="Name"><input data-testid="curated-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className={inputCls} /></Field>
                <Field label="Email"><input data-testid="curated-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@domain.com" className={inputCls} /></Field>
                <Field label="Phone"><input data-testid="curated-phone" type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="With country code" className={inputCls} /></Field>
              </div>

              {err ? <p className="text-[#A33A2A] text-[13px]" data-testid="curated-err">{err}</p> : null}
              <p className="text-xs text-lux-muted leading-relaxed">
                By submitting, you agree to our <Link to="/privacy" className="text-lux-accent underline">Privacy Policy</Link> and <Link to="/service-terms" className="text-lux-accent underline">Service &amp; Enquiry Terms</Link>.
              </p>
              <div className="mt-2"><ButtonLux label={busy ? "Sending…" : (form.cta_label || "Curate My Gift")} onClick={submit} disabled={busy} testId="curated-submit" /></div>
            </div>
          )}
        </div>
      </Container>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
